"use client";

import SpinnerMini from "@/components/my-ui/fallbacks/SpinnerMini";
import PackageDetailModal from "@/components/features/packages/PackageDetailModal";
import { getTotalCartItems } from "@/redux/slices/cartSlice";
import { useAppSelector } from "@/redux/hooks";
import type { Package } from "@/types/packages/data-only/DataOnlyCountryPackages";
import { cn } from "@/lib/utils";
import { ChevronRight, Minus, Plus } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { toast } from "sonner";

type PropsType = {
  selectedPackage: Package;
  countryFlag: string;
  isCheckoutPageLoading: boolean;
  cartQuantity: number;
  setCartQuantity: React.Dispatch<React.SetStateAction<number>>;
  onAddToCart: () => void;
  onBuyNow: () => void;
};

export function ProductCart({
  selectedPackage,
  countryFlag,
  isCheckoutPageLoading,
  cartQuantity,
  setCartQuantity,
  onAddToCart,
  onBuyNow,
}: PropsType) {
  const t = useTranslations("Shared.productCart");
  const tPackages = useTranslations("Shared.countryPackages");
  const [showPackageDetail, setShowPackageDetail] = useState(false);
  const totalCartQuantity = useAppSelector((state) =>
    getTotalCartItems(state.cart),
  );
  const isCartFull = totalCartQuantity + cartQuantity >= 5;

  const {
    data_quantity,
    data_unit,
    package_validity,
    package_validity_unit,
    unlimited,
    price,
    discounted_price,
    discount_percentage,
  } = selectedPackage;

  const packageLabel = unlimited
    ? `${t("unlimited")} ${data_unit ? data_unit : t("data")}`
    : `${data_quantity}${data_unit}`;

  const validityLabel = `${package_validity} ${package_validity_unit}`;
  const displayPrice = discount_percentage > 0 ? discounted_price : price;

  function handleDecrease() {
    if (cartQuantity === 1) return;
    setCartQuantity((quantity) => quantity - 1);
  }

  function handleIncrease() {
    if (isCartFull) {
      toast.error(t("cartFull"));
      return;
    }
    setCartQuantity((quantity) => quantity + 1);
  }

  return (
    <div
      className={cn(
        "fixed bottom-0 left-1/2 z-50 w-full -translate-x-1/2",
        "md:bottom-4 md:w-auto md:max-w-[calc(100%-2rem)]",
      )}
    >
      {/* Mobile: stacked */}
      <div className="flex w-full flex-col md:hidden">
        <div className="flex items-center gap-2.5 bg-foreground px-3 py-2 dark:bg-muted">
          <div className="relative size-9 shrink-0">
            <Image
              src={countryFlag || "/placeholder.svg"}
              alt="Country flag"
              fill
              className="rounded-full object-cover"
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2 p-1.5">
            <div className="min-w-0 flex-1">
              <p className="truncate font-poppins text-sm font-600 leading-tight text-primary-foreground">
                {packageLabel} · {validityLabel}
              </p>
              <p className="truncate font-poppins text-sm font-600 leading-tight text-primary-foreground">
                ${displayPrice}
              </p>
            </div>
            <DetailsButton onClick={() => setShowPackageDetail(true)}>
              {t("details")}
            </DetailsButton>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-card px-3 py-3 pb-6">
          <QuantityControl
            cartQuantity={cartQuantity}
            onDecrease={handleDecrease}
            onIncrease={handleIncrease}
            className="h-[38px] min-w-[100px] flex-1"
            textClassName="text-base tracking-[-0.32px]"
          />
          <AddToCartButton
            label={tPackages("addToCart")}
            onClick={onAddToCart}
            className="h-[38px] min-w-0 flex-1 text-sm"
          />
          <BuyNowButton
            label={tPackages("buyNow")}
            onClick={onBuyNow}
            loading={isCheckoutPageLoading}
            className="h-[38px] min-w-0 flex-1 text-sm"
          />
        </div>
      </div>

      {/* Tablet / Desktop: overlapping docks per Figma */}
      <div className="hidden items-stretch md:flex">
        <div
          className={cn(
            "relative z-10 flex items-center gap-2.5 rounded-[24px] py-1.5 pl-3.5 pr-4",
            "bg-foreground shadow-[0px_-3px_20px_rgba(0,0,0,0.07)]",
            "dark:border dark:border-foreground/10 dark:bg-muted dark:shadow-[0px_-3px_20px_rgba(0,0,0,0.45)]",
          )}
        >
          <div className="relative size-10 shrink-0">
            <Image
              src={countryFlag || "/placeholder.svg"}
              alt="Country flag"
              fill
              className="rounded-full object-cover"
            />
          </div>
          <div className="flex flex-col gap-0">
            <p className="whitespace-nowrap font-poppins text-base font-600 leading-tight text-primary-foreground">
              {packageLabel} · {validityLabel}
            </p>
            <p className="font-poppins text-sm font-500 leading-tight text-primary-foreground">
              ${displayPrice}
            </p>
            <DetailsButton onClick={() => setShowPackageDetail(true)}>
              {t("details")}
            </DetailsButton>
          </div>
        </div>

        <div
          className={cn(
            "-ms-7 flex items-center gap-3 rounded-e-[24px] border border-dashed border-border bg-card py-1.5 pl-10 pr-4 shadow-[0px_-3px_20px_rgba(0,0,0,0.07)]",
            "dark:border-foreground/10 dark:shadow-[0px_-3px_20px_rgba(0,0,0,0.45)]",
          )}
        >
          <QuantityControl
            cartQuantity={cartQuantity}
            onDecrease={handleDecrease}
            onIncrease={handleIncrease}
            className="h-11 w-[120px] shrink-0"
            textClassName="text-lg tracking-[-0.4px]"
          />
          <AddToCartButton
            label={tPackages("addToCart")}
            onClick={onAddToCart}
            className="h-11 w-[154px] shrink-0 text-sm"
          />
          <BuyNowButton
            label={tPackages("buyNow")}
            onClick={onBuyNow}
            loading={isCheckoutPageLoading}
            className="h-11 w-[134px] shrink-0 text-sm"
          />
        </div>
      </div>

      <PackageDetailModal
        packageDetail={selectedPackage}
        countryFlag={countryFlag}
        open={showPackageDetail}
        onOpenChange={setShowPackageDetail}
      />
    </div>
  );
}

function DetailsButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-fit shrink-0 items-center gap-0.5 text-sm font-400 leading-tight text-primary-foreground/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {children}
      <ChevronRight
        className="size-3.5 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
        aria-hidden
      />
    </button>
  );
}

function AddToCartButton({
  label,
  onClick,
  className,
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "flex items-center justify-center rounded-xl border border-solid border-border bg-transparent px-2 font-poppins font-600 text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "dark:border-foreground/20",
        className,
      )}
    >
      {label}
    </button>
  );
}

function BuyNowButton({
  label,
  onClick,
  loading,
  className,
}: {
  label: string;
  onClick: () => void;
  loading: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={loading}
      className={cn(
        "flex items-center justify-center rounded-xl bg-primary px-2 font-poppins font-600 text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        "dark:bg-primary-accent dark:hover:bg-primary-accent/90",
        className,
      )}
    >
      {loading ? <SpinnerMini /> : label}
    </button>
  );
}

function QuantityControl({
  cartQuantity,
  onDecrease,
  onIncrease,
  className,
  textClassName,
}: {
  cartQuantity: number;
  onDecrease: () => void;
  onIncrease: () => void;
  className?: string;
  textClassName?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-3.5 rounded-xl bg-muted",
        className,
      )}
    >
      <button
        type="button"
        aria-label="Decrease quantity"
        onClick={onDecrease}
        className="rounded-sm p-0.5 text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Minus className="size-6" strokeWidth={1.5} />
      </button>
      <span
        className={cn(
          "w-[18px] text-center font-sans font-400 leading-[1.4] text-foreground",
          textClassName,
        )}
      >
        {cartQuantity}
      </span>
      <button
        type="button"
        aria-label="Increase quantity"
        onClick={onIncrease}
        className="rounded-sm p-0.5 text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Plus className="size-6" strokeWidth={1.5} />
      </button>
    </div>
  );
}
